import React from 'react';
import { FormField } from '../../services/formTemplateService';
import { Dropdown } from '../ui';
import { useTranslation } from 'react-i18next';

interface DynamicFormFieldProps {
  field: FormField;
  value: any;
  onChange: (fieldId: string, value: any) => void;
  error?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * DynamicFormField Component
 *
 * Renders a form field dynamically based on its type.
 * Supports all field types: text, email, tel, textarea, select, radio, checkbox,
 * multiselect, date, time, number, name, companySize
 *
 * @param field - Field configuration from FormTemplate
 * @param value - Current field value
 * @param onChange - Callback when value changes
 * @param error - Validation error message
 */
const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
  field,
  value,
  onChange,
  error,
  className = '',
  disabled = false,
}) => {
  const { t } = useTranslation();

  /**
   * Get translated label for common field labels
   * Matches by field.label to handle UUID field IDs
   * Falls back to field.label for custom fields
   */
  const getFieldLabel = (): string => {
    // Map common English labels to translation keys
    const labelToTranslationKey: Record<string, string> = {
      'First Name': 'forms:fields.firstName',
      'Last Name': 'forms:fields.lastName',
      'Full Name': 'forms:fields.fullName',
      'Name': 'forms:fields.fullName',
      'Email': 'forms:fields.email',
      'Email Address': 'forms:fields.email',
      'Phone': 'forms:fields.phone',
      'Phone Number': 'forms:fields.phone',
      'Notes': 'forms:fields.notes',
      'Additional notes': 'forms:fields.notes',
      'Meeting Notes': 'forms:fields.meetingNotes',
      // In `forms:` like every other field. It used to be the one entry
      // pointing at the `booking:` namespace, which is how its PLACEHOLDER
      // came to reference a key nobody ever created — see below.
      'Company': 'forms:fields.company',
    };

    // Try to match by label (case-insensitive)
    const matchedKey = Object.keys(labelToTranslationKey).find(
      key => key.toLowerCase() === field.label.toLowerCase()
    );

    if (matchedKey) {
      const translationKey = labelToTranslationKey[matchedKey];
      return t(translationKey, field.label); // Use translation with fallback
    }

    return field.label; // Return original label for custom fields
  };

  /**
   * Get translated placeholder for common field labels
   * Falls back to field.placeholder for custom fields
   */
  const getFieldPlaceholder = (): string | undefined => {
    // Map common English labels to placeholder translation keys
    const labelToPlaceholderKey: Record<string, string> = {
      'First Name': 'forms:placeholders.firstName',
      'Last Name': 'forms:placeholders.lastName',
      'Full Name': 'forms:placeholders.fullName',
      'Name': 'forms:placeholders.fullName',
      'Email': 'forms:placeholders.email',
      'Email Address': 'forms:placeholders.email',
      'Phone': 'forms:placeholders.phone',
      'Phone Number': 'forms:placeholders.phone',
      'Notes': 'forms:placeholders.notes',
      'Additional notes': 'forms:placeholders.notes',
      'Meeting Notes': 'forms:placeholders.meetingNotes',
      /**
       * ⚠️ This pointed at `booking:placeholders.company`, and that key has
       * never existed in ANY language — `booking.placeholders` is an empty
       * object in both files. So the lookup missed for every visitor and fell
       * through to `field.placeholder`, which is the English string stored on
       * the form template ("Company name"). A Japanese booking page showed a
       * Japanese label above an English hint, and only on this one field,
       * because every other field resolves inside `forms:` where the keys are
       * actually defined.
       */
      'Company': 'forms:placeholders.company',
    };

    // Try to match by label (case-insensitive)
    const matchedKey = Object.keys(labelToPlaceholderKey).find(
      key => key.toLowerCase() === field.label.toLowerCase()
    );

    if (matchedKey) {
      const translationKey = labelToPlaceholderKey[matchedKey];
      return t(translationKey, field.placeholder || '');
    }

    return field.placeholder;
  };

  const fieldLabel = getFieldLabel();
  const fieldPlaceholder = getFieldPlaceholder();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    onChange(field.id, e.target.value);
  };

  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const currentValues = Array.isArray(value) ? value : [];
    const newValues = checked
      ? [...currentValues, optionValue]
      : currentValues.filter((v: string) => v !== optionValue);
    onChange(field.id, newValues);
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    onChange(field.id, selectedOptions);
  };

  // Map width property to Tailwind grid column classes
  const getWidthClass = () => {
    const width = (field as any).width || 'full';
    switch (width) {
      case 'half':
        return 'col-span-12 md:col-span-6'; // Full width on mobile, half on desktop
      case 'third':
        return 'col-span-12 md:col-span-4'; // Full width on mobile, third on desktop
      case 'full':
      default:
        return 'col-span-12'; // Full width always
    }
  };

  // Combine width class with custom className
  const wrapperClasses = `${getWidthClass()} ${className}`;

  // Base input classes with error and disabled state
  const inputClasses = `block w-full px-3 py-2 border ${
    error ? 'border-red-500' : 'border-gray-300'
  } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm${
    disabled ? ' bg-gray-50 text-gray-500 cursor-not-allowed' : ''
  }`;

  // Render field based on type
  switch (field.type) {
    case 'name':
      // Special name field that handles first/last name
      return (
        <div className={wrapperClasses}>
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-1">{field.helpText}</p>
          )}
          <input
            type="text"
            id={field.id}
            name={field.id}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
            disabled={disabled}
            placeholder={fieldPlaceholder || t('forms:placeholders.fullName', 'Enter your name')}
            className={inputClasses}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    case 'companySize':
      // Special company size field with predefined options
      return (
        <div className={wrapperClasses}>
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-1">{field.helpText}</p>
          )}
          <Dropdown
            id={field.id}
            name={field.id}
            value={value || ''}
            onChange={(newValue) => onChange(field.id, newValue)}
            options={field.options || []}
            placeholder={fieldPlaceholder || t('common:form.selectOption', 'Select an option')}
            error={!!error}
            disabled={disabled}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    case 'text':
    case 'email':
    case 'tel':
    case 'number':
      return (
        <div className={wrapperClasses}>
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-1">{field.helpText}</p>
          )}
          <input
            type={field.type}
            id={field.id}
            name={field.id}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
            disabled={disabled}
            placeholder={fieldPlaceholder}
            className={inputClasses}
            min={field.validation?.min}
            max={field.validation?.max}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            pattern={field.validation?.pattern}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    case 'textarea':
      return (
        <div className={wrapperClasses}>
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-1">{field.helpText}</p>
          )}
          <textarea
            id={field.id}
            name={field.id}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
            disabled={disabled}
            placeholder={fieldPlaceholder}
            rows={3}
            className={inputClasses}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    case 'select':
      return (
        <div className={wrapperClasses}>
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-1">{field.helpText}</p>
          )}
          <Dropdown
            id={field.id}
            name={field.id}
            value={value || ''}
            onChange={(newValue) => onChange(field.id, newValue)}
            options={field.options || []}
            placeholder={fieldPlaceholder || t('common:form.selectOption', 'Select an option')}
            error={!!error}
            disabled={disabled}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    case 'multiselect':
      return (
        <div className={wrapperClasses}>
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-1">{field.helpText}</p>
          )}
          <select
            id={field.id}
            name={field.id}
            value={Array.isArray(value) ? value : []}
            onChange={handleMultiSelectChange}
            required={field.required}
            disabled={disabled}
            multiple
            className={inputClasses}
            size={Math.min(field.options?.length || 5, 5)}
          >
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple options</p>
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    case 'radio':
      return (
        <div className={wrapperClasses}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-2">{field.helpText}</p>
          )}
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="radio"
                  id={`${field.id}-${option.value}`}
                  name={field.id}
                  value={option.value}
                  checked={value === option.value}
                  onChange={handleChange}
                  required={field.required}
                  disabled={disabled}
                  className="h-4 w-4 border-gray-300 focus:ring-primary-500"
                  style={{
                    accentColor: 'var(--brand-color, #2E7AA0)',
                    cursor: 'pointer'
                  }}
                />
                <label
                  htmlFor={`${field.id}-${option.value}`}
                  className="ml-2 text-sm text-gray-700 cursor-pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    case 'checkbox':
      return (
        <div className={wrapperClasses}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-2">{field.helpText}</p>
          )}
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  id={`${field.id}-${option.value}`}
                  name={field.id}
                  value={option.value}
                  checked={Array.isArray(value) && value.includes(option.value)}
                  onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                  disabled={disabled}
                  className="h-4 w-4 border-gray-300 rounded focus:ring-primary-500"
                  style={{
                    accentColor: 'var(--brand-color, #2E7AA0)',
                    cursor: 'pointer'
                  }}
                />
                <label
                  htmlFor={`${field.id}-${option.value}`}
                  className="ml-2 text-sm text-gray-700 cursor-pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    case 'date':
      return (
        <div className={wrapperClasses}>
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-1">{field.helpText}</p>
          )}
          <input
            type="date"
            id={field.id}
            name={field.id}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
            disabled={disabled}
            className={inputClasses}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    case 'time':
      return (
        <div className={wrapperClasses}>
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
            {fieldLabel}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {field.helpText && (
            <p className="text-xs text-gray-500 mb-1">{field.helpText}</p>
          )}
          <input
            type="time"
            id={field.id}
            name={field.id}
            value={value || ''}
            onChange={handleChange}
            required={field.required}
            disabled={disabled}
            className={inputClasses}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600 animate-slideDown">{error}</p>
          )}
        </div>
      );

    default:
      return null;
  }
};

export default DynamicFormField;
